export type MarkdownInline =
  | { kind: "text"; text: string }
  | { kind: "break" }
  | { kind: "code"; text: string }
  | { kind: "strong" | "em" | "delete"; children: MarkdownInline[] }
  | { kind: "link"; href: string; children: MarkdownInline[] };

export interface MarkdownListItem {
  children: MarkdownInline[];
  checked?: boolean;
}

export type MarkdownBlock =
  | { kind: "paragraph"; children: MarkdownInline[] }
  | { kind: "heading"; level: 1 | 2 | 3; children: MarkdownInline[] }
  | { kind: "list"; ordered: boolean; items: MarkdownListItem[] }
  | { kind: "quote"; blocks: MarkdownBlock[] }
  | { kind: "code"; text: string; lang?: string }
  | { kind: "rule" };

const LIST_RE = /^ {0,3}(?:(\d+)[.)]|([-+*]))\s+(.*)$/;
const HEADING_RE = /^ {0,3}(#{1,3})\s+(.+?)\s*#*\s*$/;
const QUOTE_RE = /^ {0,3}>\s?(.*)$/;
const FENCE_RE = /^ {0,3}```\s*([\w-]+)?\s*$/;
const FENCE_END_RE = /^ {0,3}```\s*$/;
const RULE_RE = /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/;

export function parseMarkdown(input: string): MarkdownBlock[] {
  const lines = input.replace(/\r\n?/g, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? "";
    if (isBlank(line)) {
      i++;
      continue;
    }

    const fence = FENCE_RE.exec(line);
    if (fence) {
      const lang = fence[1]?.trim();
      const body: string[] = [];
      i++;
      while (i < lines.length && !FENCE_END_RE.test(lines[i] ?? "")) {
        body.push(lines[i] ?? "");
        i++;
      }
      if (i < lines.length) i++;
      const text = body.join("\n");
      blocks.push(lang ? { kind: "code", text, lang } : { kind: "code", text });
      continue;
    }

    if (RULE_RE.test(line)) {
      blocks.push({ kind: "rule" });
      i++;
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      const level = heading[1]!.length as 1 | 2 | 3;
      blocks.push({ kind: "heading", level, children: parseInline(heading[2] ?? "") });
      i++;
      continue;
    }

    const quote = QUOTE_RE.exec(line);
    if (quote) {
      const body: string[] = [];
      while (i < lines.length) {
        const quoted = QUOTE_RE.exec(lines[i] ?? "");
        if (!quoted) break;
        body.push(quoted[1] ?? "");
        i++;
      }
      blocks.push({ kind: "quote", blocks: parseMarkdown(body.join("\n")) });
      continue;
    }

    const list = LIST_RE.exec(line);
    if (list) {
      const parsed = parseList(lines, i, !!list[1]);
      blocks.push(parsed.block);
      i = parsed.next;
      continue;
    }

    const paragraphLines = [line.trimEnd()];
    i++;
    while (i < lines.length) {
      const next = lines[i] ?? "";
      if (isBlank(next) || isBlockStart(next)) break;
      paragraphLines.push(next.trimEnd());
      i++;
    }
    blocks.push({ kind: "paragraph", children: parseInline(paragraphLines.join("\n").trim()) });
  }

  return blocks;
}

export function hasMarkdownSyntax(input: string): boolean {
  return (
    /^ {0,3}(#{1,3}\s|>\s?|```|([-+*]|\d+[.)])\s+)/m.test(input) ||
    /^ {0,3}([-*_])(?:\s*\1){2,}\s*$/m.test(input) ||
    /(^|[^\\])(`[^`\n]+`|\*\*[^*\n]+\*\*|__[^_\n]+__|~~[^~\n]+~~|\[[^\]\n]+\]\([^)]+\))/.test(input) ||
    /(^|\s)([*_])\S(?:.*?\S)?\2(?=\s|$|[.,;:!?)]?)/.test(input)
  );
}

export function normalizeMarkdownHref(raw: string): string | null {
  const href = raw.trim();
  if (!href || /[\u0000-\u001f\u007f\s]/.test(href)) return null;

  try {
    const url = new URL(href);
    return ["http:", "https:", "mailto:", "tel:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

function parseList(lines: string[], start: number, ordered: boolean): { block: MarkdownBlock; next: number } {
  const items: MarkdownListItem[] = [];
  let i = start;

  while (i < lines.length) {
    const match = LIST_RE.exec(lines[i] ?? "");
    if (!match || !!match[1] !== ordered) break;

    const itemLines = [match[3] ?? ""];
    i++;

    while (i < lines.length) {
      const line = lines[i] ?? "";
      if (isBlank(line)) {
        i++;
        break;
      }
      if (LIST_RE.test(line)) break;
      const continuation = /^ {2,}(.*)$/.exec(line);
      if (!continuation) break;
      itemLines.push(continuation[1] ?? "");
      i++;
    }

    const raw = itemLines.join("\n").trim();
    const checkbox = /^\[( |x|X)\]\s+(.*)$/s.exec(raw);
    if (checkbox) {
      items.push({ checked: checkbox[1] !== " ", children: parseInline(checkbox[2] ?? "") });
    } else {
      items.push({ children: parseInline(raw) });
    }
  }

  return { block: { kind: "list", ordered, items }, next: i };
}

function parseInline(input: string): MarkdownInline[] {
  const out: MarkdownInline[] = [];
  let text = "";
  let i = 0;

  const pushText = () => {
    if (!text) return;
    const last = out[out.length - 1];
    if (last?.kind === "text") last.text += text;
    else out.push({ kind: "text", text });
    text = "";
  };

  while (i < input.length) {
    const char = input.charAt(i);
    if (char === "\n") {
      pushText();
      out.push({ kind: "break" });
      i++;
      continue;
    }

    if (char === "`") {
      const end = input.indexOf("`", i + 1);
      if (end > i + 1) {
        pushText();
        out.push({ kind: "code", text: input.slice(i + 1, end) });
        i = end + 1;
        continue;
      }
    }

    if (char === "[") {
      const labelEnd = input.indexOf("]", i + 1);
      if (labelEnd > i + 1 && input.charAt(labelEnd + 1) === "(") {
        const hrefEnd = input.indexOf(")", labelEnd + 2);
        if (hrefEnd > labelEnd + 2) {
          const href = normalizeMarkdownHref(input.slice(labelEnd + 2, hrefEnd));
          if (href) {
            pushText();
            out.push({ kind: "link", href, children: parseInline(input.slice(i + 1, labelEnd)) });
          } else {
            text += input.slice(i, hrefEnd + 1);
          }
          i = hrefEnd + 1;
          continue;
        }
      }
    }

    const marker = inlineMarkerAt(input, i);
    if (marker && canOpen(input, i, marker.text)) {
      const end = findClosingMarker(input, marker.text, i + marker.text.length);
      if (end > i + marker.text.length) {
        pushText();
        out.push({ kind: marker.kind, children: parseInline(input.slice(i + marker.text.length, end)) });
        i = end + marker.text.length;
        continue;
      }
    }

    text += char;
    i++;
  }

  pushText();
  return out;
}

function isBlank(line: string): boolean {
  return line.trim() === "";
}

function isBlockStart(line: string): boolean {
  return FENCE_RE.test(line) || RULE_RE.test(line) || HEADING_RE.test(line) || QUOTE_RE.test(line) || LIST_RE.test(line);
}

function inlineMarkerAt(input: string, index: number): { text: string; kind: "strong" | "em" | "delete" } | null {
  const two = input.slice(index, index + 2);
  if (two === "**" || two === "__") return { text: two, kind: "strong" };
  if (two === "~~") return { text: two, kind: "delete" };
  const one = input.charAt(index);
  if (one === "*" || one === "_") return { text: one, kind: "em" };
  return null;
}

function canOpen(input: string, index: number, marker: string): boolean {
  const after = input.charAt(index + marker.length);
  if (!after || /\s/.test(after)) return false;
  if (marker.includes("_") && isWord(input.charAt(index - 1)) && isWord(after)) return false;
  return true;
}

function findClosingMarker(input: string, marker: string, start: number): number {
  let pos = start;
  while (pos < input.length) {
    const index = input.indexOf(marker, pos);
    if (index === -1) return -1;
    const before = input.charAt(index - 1);
    const after = input.charAt(index + marker.length);
    if (!/\s/.test(before) && !(marker.includes("_") && isWord(before) && isWord(after))) return index;
    pos = index + marker.length;
  }
  return -1;
}

function isWord(char: string): boolean {
  return /^[A-Za-z0-9]$/.test(char);
}
